/**
 * Children Form Component
 */

import React from 'react';
import { FormData } from '../types';

interface ChildrenFormProps {
  formData: FormData;
  onChange: (field: keyof FormData, value: any) => void;
  onNext: () => void;
  onBack: () => void;
}

const ChildrenForm: React.FC<ChildrenFormProps> = ({ formData, onChange, onNext, onBack }) => {
  const hasChildren = formData.hasChildren;

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Children Information
      </h2>

      <div className="space-y-6">
        {/* Has Children */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Do you have children together? <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-4">
            <button
              onClick={() => onChange('hasChildren', true)}
              className={`flex-1 py-3 px-6 rounded-lg font-medium transition duration-300 ${
                hasChildren
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Yes
            </button>
            <button
              onClick={() => {
                onChange('hasChildren', false);
                onChange('numberOfChildren', 0);
                onChange('primaryCaregiver', 'joint');
                onChange('hasSpecialNeeds', false);
              }}
              className={`flex-1 py-3 px-6 rounded-lg font-medium transition duration-300 ${
                !hasChildren
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              No
            </button>
          </div>
        </div>

        {hasChildren && (
          <>
            {/* Number of Children */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Number of Children <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                value={formData.numberOfChildren || 0}
                onChange={(e) => onChange('numberOfChildren', Number(e.target.value))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Primary Caregiver */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Caregiver <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.primaryCaregiver || 'joint'}
                onChange={(e) => onChange('primaryCaregiver', e.target.value as 'user' | 'spouse' | 'joint')}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="joint">Joint / Shared</option>
                <option value="user">You</option>
                <option value="spouse">Spouse</option>
              </select>
              <p className="text-gray-500 text-sm mt-1">
                The parent who provides most of the daily care for the children
              </p>
            </div>

            {/* Special Needs */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Do any children have special needs?
              </label>
              <div className="flex gap-4">
                <button
                  onClick={() => onChange('hasSpecialNeeds', true)}
                  className={`flex-1 py-3 px-6 rounded-lg font-medium transition duration-300 ${
                    formData.hasSpecialNeeds
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Yes
                </button>
                <button
                  onClick={() => onChange('hasSpecialNeeds', false)}
                  className={`flex-1 py-3 px-6 rounded-lg font-medium transition duration-300 ${
                    !formData.hasSpecialNeeds
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  No
                </button>
              </div>
            </div>

            {/* Information Box */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-2">
                💡 How Children Affect Property Division
              </h3>
              <p className="text-sm text-blue-800">
                Courts often consider child custody arrangements when dividing property.
                The primary caregiver may receive additional consideration to ensure stability
                for the children. Special needs children may also influence the division.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="mt-8 flex justify-between">
        <button
          onClick={onBack}
          className="bg-gray-200 text-gray-700 font-semibold py-3 px-8 rounded-lg hover:bg-gray-300 transition duration-300"
        >
          Back
        </button>
        <button
          onClick={onNext}
          className="bg-blue-600 text-white font-semibold py-3 px-8 rounded-lg hover:bg-blue-700 transition duration-300"
        >
          Next: Pre-Marital Assets
        </button>
      </div>
    </div>
  );
};

export default ChildrenForm;